import { TooltipCell } from '@/features/approving_invoice_reports/components/List/Table/Columns/Cells/components/TooltipCell/TooltipCell';
import { type CardColumn } from '@/components/CardTable';
import { useApprovalDrawerViewOptimizationFlag } from '@/hooks/feature_flag/useApprovalDrawerViewOptimizationFlag';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import { IconHelp, Tooltip } from '@moneyforward/ap-frontend-components';
import type { InvoiceReportDetailJournalEntriesResponseItem } from 'ap-openapi';
import classnames from 'classnames/bind';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

export type JournalEntryRow = InvoiceReportDetailJournalEntriesResponseItem & {
  id: string;
};

export const useColumns = (): CardColumn<JournalEntryRow>[] => {
  const { t } = useTranslation();
  const container = useGlobalContainerRef();
  const isLayoutSwitchEnabled = useApprovalDrawerViewOptimizationFlag();
  const columns: CardColumn<JournalEntryRow>[] = [
    {
      title: t('journal_entry.debit.account'),
      dataIndex: 'dr_item_name',
      key: 'dr_item_name',
      width: 128,
      group: 'debit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.debit.sub_account'),
      dataIndex: 'dr_sub_item_name',
      key: 'dr_sub_item_name',
      width: 128,
      group: 'debit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.debit.department'),
      dataIndex: 'dr_dept_name',
      key: 'dr_dept_name',
      width: 128,
      group: 'debit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.debit.project'),
      dataIndex: 'dr_project_name',
      key: 'dr_project_name',
      width: 128,
      group: 'debit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.debit.counterparty'),
      dataIndex: 'dr_counterparty_name',
      key: 'dr_counterparty_name',
      width: 128,
      group: 'debit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.debit.tax_category'),
      dataIndex: 'dr_excise_name',
      key: 'dr_excise_name',
      width: 120,
      group: 'debit',
    },
    {
      title: (
        <Tooltip
          title={t('journal_entry.debit.invoice_kind')}
          getPopupContainer={container}
        >
          <div className={cx(styles['ellipsisTitle'])}>
            {t('journal_entry.debit.invoice_kind')}
          </div>
        </Tooltip>
      ),
      dataIndex: 'dr_invoice_kind',
      key: 'dr_invoice_kind',
      width: 120,
      align: isLayoutSwitchEnabled ? 'left' : 'right',
      group: 'debit',
      cardLabel: t('journal_entry.debit.invoice_kind'),
    },
    {
      title: t('journal_entry.debit.amount_tax_included'),
      dataIndex: 'dr_display_amount',
      key: 'dr_display_amount',
      width: 136,
      align: 'right',
      group: 'debit',
    },
    {
      title: (
        <div className={cx(styles['titleWithTooltip'])}>
          <div>{t('journal_entry.debit.purchase_tax_deduction')}</div>
          <Tooltip
            title={t('journal_entry.debit.purchase_tax_deduction.tooltip')}
            getPopupContainer={container}
          >
            <span>
              <IconHelp size={14} />
            </span>
          </Tooltip>
        </div>
      ),
      dataIndex: 'dr_display_purchase_tax_credit',
      key: 'dr_display_purchase_tax_credit',
      width: 136,
      align: 'right',
      group: 'debit',
      cardLabel: t('journal_entry.debit.purchase_tax_deduction'),
      help: t('journal_entry.debit.purchase_tax_deduction.tooltip'),
    },
    {
      title: t('journal_entry.credit.account'),
      dataIndex: 'cr_item_name',
      key: 'cr_item_name',
      width: 128,
      group: 'credit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.credit.sub_account'),
      dataIndex: 'cr_sub_item_name',
      key: 'cr_sub_item_name',
      width: 128,
      group: 'credit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.credit.department'),
      dataIndex: 'cr_dept_name',
      key: 'cr_dept_name',
      width: 128,
      group: 'credit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.credit.project'),
      dataIndex: 'cr_project_name',
      key: 'cr_project_name',
      width: 128,
      group: 'credit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.credit.counterparty'),
      dataIndex: 'cr_counterparty_name',
      key: 'cr_counterparty_name',
      width: 128,
      group: 'credit',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('journal_entry.credit.tax_category'),
      dataIndex: 'cr_excise_name',
      key: 'cr_excise_name',
      width: 120,
      group: 'credit',
    },
    {
      title: (
        <Tooltip
          title={t('journal_entry.credit.invoice_kind')}
          getPopupContainer={container}
        >
          <div className={cx(styles['ellipsisTitle'])}>
            {t('journal_entry.credit.invoice_kind')}
          </div>
        </Tooltip>
      ),
      dataIndex: 'cr_invoice_kind',
      key: 'cr_invoice_kind',
      width: 120,
      group: 'credit',
      cardLabel: t('journal_entry.credit.invoice_kind'),
    },
    {
      title: t('journal_entry.credit.amount_tax_included'),
      dataIndex: 'cr_display_amount',
      key: 'cr_display_amount',
      width: 136,
      align: 'right',
      group: 'credit',
    },
    {
      title: (
        <div className={cx(styles['titleWithTooltip'])}>
          <div>{t('journal_entry.credit.purchase_tax_deduction')}</div>
          <Tooltip
            title={t('journal_entry.credit.purchase_tax_deduction.tooltip')}
            getPopupContainer={container}
            placement='topRight'
          >
            <span>
              <IconHelp size={14} />
            </span>
          </Tooltip>
        </div>
      ),
      dataIndex: 'cr_display_purchase_tax_credit',
      key: 'cr_display_purchase_tax_credit',
      width: 136,
      align: 'right',
      group: 'credit',
      cardLabel: t('journal_entry.credit.purchase_tax_deduction'),
      help: t('journal_entry.credit.purchase_tax_deduction.tooltip'),
    },
  ];
  return columns;
};
